/**
 * User Profile Service
 * Calls /api/* endpoints via Python Gateway proxy
 */

import type { UserProfile, FeedbackPayload, UserProfileError, UserProfileErrorCode } from '../types';
import api from './api';
import { msalInstance } from '../auth/msalConfig';

const USER_PROFILE_PATH = '/Graph';
const APPLICATION_DISCLAIMER_PATH = '/ApplicationDisclaimer';
const FEEDBACK_PATH = '/ApplicationFeedback';
const APPLICATION_THEMES_PATH = '/ApplicationThemes';
const APPLICATION_TRANSLATOR_PATH = '/ApplicationTranslator';
const APPLICATION_SETTINGS_PATH = '/ApplicationSettings';
const CLARA_AI_INTEGRATION_ID = '550e8400-e29b-41d4-a716-446655440000'; // Clara AI system identifier

interface ApplicationDisclaimerContent {
  id: string;
  content: string;
  title: string;
  timeoutMs: number;
}

function generateGuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}

// The Python proxy wraps Graph responses in { Success, Data, Message, messageKey }.
interface GraphMeProxyResponse {
  Success: boolean;
  Data: {
    id?: string;
    displayName?: string;
    mail?: string;
    userPrincipalName?: string;
    jobTitle?: string;
    avatarUrl?: string;
  };
  Message?: string;
}

export async function fetchUserProfile(): Promise<UserProfile> {
  // MSAL account is the reliable fallback when backend serializes JToken as [].
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

  const response = await api.fetch<GraphMeProxyResponse>(`${USER_PROFILE_PATH}/Me`, { method: 'GET' });
  const d = response?.Data ?? {};

  return {
    id: asString(d.id) || account?.localAccountId || '',
    displayName: asString(d.displayName) || account?.name || '',
    email: asString(d.mail) || asString(d.userPrincipalName) || account?.username || '',
    title: asString(d.jobTitle),
    avatarUrl: d.avatarUrl,
  } as UserProfile;
}

/** Extract string value safely — handles the empty-array [] the backend emits for JToken values. */
function asString(val: unknown): string {
  if (typeof val === 'string') return val;
  if (Array.isArray(val) && val.length > 0) return String(val[0]);
  return '';
}

function parseApplicationDisclaimerResponse(response: unknown): Record<string, unknown> {
  if (typeof response === 'string') {
    try {
      const parsed = JSON.parse(response) as unknown;
      return parseApplicationDisclaimerResponse(parsed);
    } catch {
      return { Content: response };
    }
  }

  if (response && typeof response === 'object' && !Array.isArray(response)) {
    return response as Record<string, unknown>;
  }

  return {};
}

function asTimeoutMs(value: unknown): number {
  const seconds = Number.parseInt(asString(value), 10);
  return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : 0;
}

function normalizeApplicationDisclaimer(response: unknown): ApplicationDisclaimerContent {
  const rawDisclaimer = parseApplicationDisclaimerResponse(response);
  const disclaimer = parseApplicationDisclaimerResponse(rawDisclaimer.Data ?? rawDisclaimer.data ?? rawDisclaimer);
  const properties = parseApplicationDisclaimerResponse(disclaimer.Properties ?? disclaimer.properties);

  return {
    id: asString(disclaimer.Id) || asString(disclaimer.id) || 'application-disclaimer',
    title: asString(disclaimer.Title) || asString(disclaimer.title) || asString(disclaimer.Name) || 'Disclaimer',
    content:
      asString(properties.TranslatedText) ||
      asString(properties.translatedText) ||
      asString(disclaimer.Content) ||
      asString(disclaimer.content) ||
      asString(disclaimer.DisclaimerContent) ||
      asString(disclaimer.disclaimerContent) ||
      asString(disclaimer.DisclaimerText) ||
      asString(disclaimer.disclaimerText) ||
      asString(disclaimer.Description) ||
      asString(disclaimer.description),
    timeoutMs: asTimeoutMs(disclaimer.Timeout ?? disclaimer.timeout),
  };
}

// Disclaimer flow: POST context to the Core API, then store acknowledgment locally when user agrees.
export async function getApplicationDisclaimer(): Promise<ApplicationDisclaimerContent> {
  const response = await api.fetch<unknown>(`${APPLICATION_DISCLAIMER_PATH}`, {
    method: 'POST',
    body: JSON.stringify([]),
  });

  return normalizeApplicationDisclaimer(response);
}

const DISCLAIMER_ACCEPTED_KEY_PREFIX = 'kci.disclaimer.accepted.';
const DISCLAIMER_EXPIRY_KEY_PREFIX = 'kci.disclaimer.expiry.';

function getDisclaimerStorageKey(userId?: string): string {
  return `${DISCLAIMER_EXPIRY_KEY_PREFIX}${userId || 'anonymous'}`;
}

export function hasDisclaimerBeenAcknowledged(userId: string): boolean {
  return getDisclaimerExpiry(userId) > Date.now();
}

export function markDisclaimerAcknowledged(userId: string): void {
  if (!userId) return;
  sessionStorage.setItem(`${DISCLAIMER_ACCEPTED_KEY_PREFIX}${userId}`, 'true');
}

export function getDisclaimerExpiry(userId?: string): number {
  const expiry = sessionStorage.getItem(getDisclaimerStorageKey(userId));
  const expiryTime = expiry ? Number.parseInt(expiry, 10) : 0;
  return Number.isFinite(expiryTime) ? expiryTime : 0;
}

export function setDisclaimerExpiry(userId: string | undefined, timeoutMs: number): void {
  if (timeoutMs <= 0) return;
  sessionStorage.setItem(getDisclaimerStorageKey(userId), String(Date.now() + timeoutMs));
}

export async function acknowledgeApplicationDisclaimer(userId?: string, timeoutMs = 0): Promise<void> {
  await api.fetch<void>(`${APPLICATION_DISCLAIMER_PATH}`, {
    method: 'POST',
    body: JSON.stringify([
      {
        key: 'AcknowledgedAt',
        value: new Date().toISOString(),
      },
    ]),
  });
  setDisclaimerExpiry(userId, timeoutMs);
  if (userId) markDisclaimerAcknowledged(userId);
}

export async function submitFeedback(payload: FeedbackPayload): Promise<{ id: string }> {
  return api.fetch<{ id: string }>(`${FEEDBACK_PATH}/AddFeedback`, {
    method: 'POST',
    body: JSON.stringify({
      Category: payload.feedbackType,
      Rating: String(payload.rating),
      Description: payload.feedback,
      Id: generateGuid(),
      AIIntegrationID: CLARA_AI_INTEGRATION_ID,
      CreatedBy: '',
      HostName: '',
      UserCountry: '',
      Context: null,
    }),
  });
}

// Theme Update Endpoints - expects UUID string ID
export async function updateTheme(themeId: string): Promise<void> {
  return api.fetch<void>(`${APPLICATION_THEMES_PATH}/${themeId}`, {
    method: 'POST',
    body: JSON.stringify([
      {
        key: 'ThemeId',
        value: themeId,
      },
    ]),
  });
}

// Get all available themes - returns Id and Name mapping
export async function getAvailableThemes(): Promise<{ Id: string; Name: string }[]> {
  return api.fetch<{ Id: string; Name: string }[]>(`${APPLICATION_THEMES_PATH}`, {
    method: 'GET',
  });
}

// Get all theme icons
export async function getAllThemeIcons(): Promise<Record<string, string>> {
  return api.fetch<Record<string, string>>(`${APPLICATION_THEMES_PATH}/GetAllIcons`, {
    method: 'POST',
  });
}

// Language/Translator Update Endpoints
export async function updateLanguage(language: string): Promise<void> {
  return api.fetch<void>(`${APPLICATION_TRANSLATOR_PATH}/${language}`, {
    method: 'POST',
    body: JSON.stringify([
      {
        key: 'Language',
        value: language,
      },
    ]),
  });
}

export interface ApplicationSettingsResponse {
  userGuideLinks?: string[];
  systemCardLinks?: string[];
  Agents?: {
    allowAgentSharing?: boolean;
    [key: string]: unknown;
  };
}

export async function getApplicationSettings(): Promise<ApplicationSettingsResponse> {
  return api.fetch<ApplicationSettingsResponse>(`${APPLICATION_SETTINGS_PATH}`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

// ── Error normalization ───────────────────────────────────────────────────────

function mapStatusToErrorCode(status?: number): UserProfileErrorCode {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 429) return 'THROTTLED';
  if (status !== undefined && status >= 500) return 'UPSTREAM_ERROR';
  return 'UNKNOWN';
}

export function normalizeUserProfileError(error: unknown): UserProfileError {
  if (error instanceof Error) {
    const httpError = error as Error & { status?: number };
    const code = mapStatusToErrorCode(httpError.status);
    return { code, status: httpError.status, message: error.message };
  }
  return { code: 'UNKNOWN', message: 'Unknown user profile failure' };
}
